<template>
  <!--
    Sin padding propio. Este `v-row` casi siempre va dentro de un `v-col`, y ahí
    sus márgenes negativos cancelan el padding de la columna que lo contiene:
    así las etiquetas quedan a la misma altura que las de los campos vecinos.
    Un `py-*` aquí rompe esa cancelación y baja el componente 12px, que fue
    justo lo que pasó la primera vez. El espaciado lo pone quien lo usa.
  -->
  <v-row justify="start" align="start">
    <v-col
      v-for="definition in definitions"
      :key="definition.type"
      cols="12"
      sm="6"
    >
      <p
        class="secondary--text pb-1 text-truncate"
        style="font-size: 14px; line-height: 20px"
      >
        {{ labelOf(definition) }}
      </p>
      <v-select
        :value="selectedNames(definition.type)"
        :items="optionsOf(definition)"
        :multiple="isMultiple(definition.type)"
        :placeholder="placeholder"
        chips
        deletable-chips
        solo
        flat
        hide-details
        background-color="#efefef"
        @change="onChange(definition.type, $event)"
      ></v-select>
    </v-col>
  </v-row>
</template>

<script>
/**
 * Selector de filtros para un contenido del catálogo.
 *
 * Recibe las definiciones tal como vienen de una colección `*Filters` de
 * Firestore —cada documento es un filtro, con `type` (el id), `name.ENG` y
 * `listFilters`— y edita el campo `filters` del contenido.
 *
 * LA FORMA DEL DATO NO ES DECORATIVA. El catálogo guarda cada valor como
 * `{ name, children: [] }`, y el backend del chat lee justo eso
 * (`names(raw.filters.goal)` en catalog.search.js). Guardar strings pelones
 * haría que el chat dejara de encontrar el contenido sin ningún error visible,
 * así que el componente convierte en los dos sentidos: muestra nombres y
 * guarda objetos.
 *
 * `children` se conserva si ya existía. Programas no los usa —su lista es
 * plana— pero presets sí tiene tres niveles, y este componente no debería ser
 * el que los pierda si algún día se usa ahí.
 */
export default {
  name: "content-filters",
  props: {
    /** Documentos de la colección de filtros, con `type` puesto desde el id. */
    definitions: {
      type: Array,
      default: () => [],
    },
    /** El campo `filters` del contenido: { goal: [{name, children}], ... } */
    value: {
      type: Object,
      default: () => ({}),
    },
    /** Tipos que aceptan varios valores. El resto guarda uno solo. */
    multiple: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: "Not set",
    },
  },
  methods: {
    labelOf(definition) {
      return (definition.name && definition.name.ENG) || definition.type;
    },
    optionsOf(definition) {
      return (definition.listFilters || []).map((option) => option.name);
    },
    isMultiple(type) {
      return this.multiple.includes(type);
    },
    /** De objetos a nombres, que es lo que el select muestra. */
    selectedNames(type) {
      const selected = (this.value && this.value[type]) || [];
      const names = selected.map((option) => option.name);
      return this.isMultiple(type) ? names : names[0] || null;
    },
    /**
     * De nombres a objetos. Se emite un `filters` nuevo en vez de mutar el que
     * llegó: el padre lo tiene en `data` y Vue 2 no reacciona a una propiedad
     * que se agrega a un objeto existente.
     */
    onChange(type, chosen) {
      const names = Array.isArray(chosen) ? chosen : [chosen];
      const definition = this.definitions.find((item) => item.type === type);

      const selected = names
        .filter((name) => name !== null && name !== undefined)
        .map((name) => {
          const original = (definition.listFilters || []).find(
            (option) => option.name === name
          );
          return { name, children: (original && original.children) || [] };
        });

      this.$emit("input", { ...this.value, [type]: selected });
    },
  },
};
</script>
